import { BaseApp } from "../app";
import { Route } from "./route";
import { ViewConstructor, View, ViewEvent } from "../view/index";
import { EventHandler } from "../event-source/index";

/** Represents a stored view event that is later used to remove an event handler. */
export interface ViewEventHandler
{
    view: View;
    eventName: string;
    handler: EventHandler;
}

/**
 * Represents a chain of views which can be navigated with the browser's history buttons.
 */
export default class ViewChain
{
    private app: BaseApp;
    private route: Route;
    private index: number;
    private currentView: View;
    private cameBackFromHistory: boolean;
    private viewEventHanlders: ViewEventHandler[];
    private static INDEX_QUIT = -1;

    private get viewTypes(): ViewConstructor[] { return this.route.viewType as ViewConstructor[]; }

    constructor(app: BaseApp, route: Route)
    {
        this.app = app;
        this.route = route;
        this.index = 0;
        this.cameBackFromHistory = false;
        this.viewEventHanlders = [];

        window.addEventListener('popstate', this.onPopState);
    }

    /**
     * Event handler that reacts to window.onpopstate event.
     */
    private onPopState = (e: PopStateEvent) =>
    {
        if (e.state && e.state.__isViewChainPopState === true)
        {
            this.cameBackFromHistory = true;
            let state: ViewChainHistoryState = e.state;

            if (state.index != ViewChain.INDEX_QUIT)
            {
                if (state.index == this.index)
                {
                    //Used to be necessary when unload event listner was not unboud before showing next view.
                    //window.history.back();
                    //console.log('Going back an additioanal step')
                }
                else
                {
                    this.show(state.index, this.currentView.data);
                    this.index = state.index;
                }
            }
            else
            {
                this.quit();
                window.history.back();
                window.history.back();
            }
        }
        else
        {
            this.quit();
        }
    };
    
    protected quit()
    {
        for(let event of this.viewEventHanlders)
        {
            event.view.off(event.eventName, event.handler);
        }

        window.removeEventListener('popstate', this.onPopState);
    }
    /**
     * Moves to the next view in the chain.
     * @param data Optional data passed to the next view.
     */
    public next(data?: any)
    {
       this.index++;
       this.show(this.index, data);
    }

    /**
     * Moves to the previous view in the chain.
     * @param data Optional data passed to the next view.
     */
    public previous(data?: any)
    {
        this.index--;
        this.show(this.index, data);
    }

    /**
     * Jumps to a specific view in the view chain by index.
     * @param index The index of the view to jump to.
     * @param data Optional data passed to the view.
     */
    private show(index: number, data?: any)
    {
        let initial = data !== undefined ? data : this.route.initial;

        if (index > -1 && index < this.viewTypes.length)
        {
            this.index = index;
            let viewConstructor = this.viewTypes[index];
            
            this.unregisterViewEvents(this.currentView);

            return this.app.view(viewConstructor, initial).then((view) =>
            {
                this.registerViewEvents(view);

                this.currentView = view;

                if (this.cameBackFromHistory)
                {
                    this.cameBackFromHistory = false;
                }
                else
                {
                    window.history.pushState(new ViewChainHistoryState(this.index), '');
                }
            });
        }
        else
        {
            window.location.hash = `#${this.app.defaultRoute}`;
        }
    }

    /** Removes view event listeners. */
    private unregisterViewEvents(view: View)
    {
        if (view !== undefined)
        {
            for(let event of this.viewEventHanlders)
            {
                if (event.view === view)
                {
                    event.view.off(ViewEvent.UNLOAD, event.handler);
                }
            }
        }
    }

    /** Adds view event listeners. */
    private registerViewEvents(view: View)
    {
        const handler = () =>
        {
            view.off(ViewEvent.UNLOAD, handler);
            this.next(view.data);
        };

        view.on(ViewEvent.UNLOAD, handler);
        this.viewEventHanlders.push({
            view: view,
            eventName: ViewEvent.UNLOAD,
            handler: handler
        });
    }

    /**
     * Starts the view chain and loads the first view.
     */
    public run()
    {
        window.history.pushState(new ViewChainHistoryState(ViewChain.INDEX_QUIT), '');
        return this.show(this.index);
    }
}

/**
 * Represents a stored ViewChain history state object.
 */
export class ViewChainHistoryState
{
    /** The index the state was created at. */
    public index: number;

    /** 
     * Identifier used to detect a ViewChainHistoryState. 
     * It would seem that history state objects are serialized; The state object
     * in popstate event is a plain Object.
     */
    public __isViewChainPopState = true;

    /**
     * Creates a new ViewChainHistory state object.
     * @param index Current index in the view chain.
     */
    constructor(index: number)
    {
        this.index = index;
    }
}